/** Round to cents — THE money-rounding helper (was reimplemented ~15x). */
export function round2(v: number): number {
  return Math.round(v * 100) / 100
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Collapse anything that rounds to zero at `digits` precision onto a true +0.
 *
 * Two ways a minus sign ends up in front of nothing. Negative zero: a $0
 * liability plotted as `0 * -1` is -0, and Intl faithfully renders that as
 * "-$0". And plain rounding: -$0.40 in a whole-dollar formatter is "-$0" while
 * +$0.40 is "$0", so the same magnitude picks up a sign purely by side. Both
 * read as a bug to anyone looking at the number, so neither should survive
 * formatting.
 */
function snapZero(v: number, digits: number): number {
  return Math.abs(v) < 0.5 * 10 ** -digits ? 0 : v
}

/** $1,234,567 — whole dollars for balances and chart labels. */
export function fmtUsd(v: number): string {
  return usd.format(snapZero(v, 0))
}

/** $12.34 — cents for transaction amounts. */
export function fmtUsdCents(v: number): string {
  return usdCents.format(snapZero(v, 2))
}

/** Compact: $1.2M / $340K / $87 — for stat tiles and axis ticks. */
export function fmtUsdCompact(value: number): string {
  const v = snapZero(value, 0)
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${trim(abs / 1_000_000)}M`
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}K`
  if (abs >= 1_000) return `${sign}$${trim(abs / 1000)}K`
  return `${sign}$${Math.round(abs)}`
}

function trim(v: number): string {
  const s = v.toFixed(2)
  return s.endsWith('00') ? String(Math.round(v)) : s.replace(/0$/, '')
}

/** Signed delta: +$1,234 / -$567. */
export function fmtDelta(value: number): string {
  const v = snapZero(value, 0)
  return (v >= 0 ? '+' : '') + usd.format(v)
}

export function fmtPct(v: number, digits = 1): string {
  return `${(v * 100).toFixed(digits)}%`
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2024-03" -> "Mar 2024" */
export function fmtMonth(key: string): string {
  const [y, m] = key.split('-').map(Number)
  return `${MONTHS_SHORT[m - 1]} ${y}`
}

/** "2024-03" -> "Mar '24" (axis ticks) */
export function fmtMonthShort(key: string): string {
  const [y, m] = key.split('-').map(Number)
  return `${MONTHS_SHORT[m - 1]} '${String(y).slice(2)}`
}

/** "2024-03-18" -> "Mar 18, 2024" */
export function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return `${MONTHS_SHORT[m - 1]} ${d}, ${y}`
}

/** Current month key of an ISO date: "2024-03-18" -> "2024-03" */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

/** Add n months to a "YYYY-MM" key. */
export function addMonths(key: string, n: number): string {
  const [y, m] = key.split('-').map(Number)
  const total = y * 12 + (m - 1) + n
  const ny = Math.floor(total / 12)
  const nm = (total % 12) + 1
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}`
}

/** Current calendar month as "YYYY-MM" (client clock). */
export function currentMonthKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Add n days to a "YYYY-MM-DD" key. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d + n)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

/** Today as "YYYY-MM-DD" (client clock). */
export function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** A date/month key ("2026-07-14" or legacy "2026-07") -> local epoch ms, for
 * plotting on a real (time-scaled) x-axis so gaps between points reflect actual
 * elapsed time instead of being evenly spaced. */
export function dateToMs(key: string): number {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1).getTime()
}

/** epoch ms -> "Mar '24" (time-axis ticks). */
export function fmtMsTick(ms: number): string {
  const d = new Date(ms)
  return `${MONTHS_SHORT[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`
}

/** epoch ms -> "Mar 18" (day-granular time-axis ticks for short windows). */
export function fmtMsTickDay(ms: number): string {
  const d = new Date(ms)
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`
}

/** stringified epoch ms -> "Mar 18, 2024" (time-axis tooltip heading). */
export function fmtMsDate(label: string): string {
  const d = new Date(Number(label))
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

/** "2026-07-14" -> "Jul 14, 2026 · 3:37 PM" style, or just the date if no time. */
export function fmtDateTime(iso: string): string {
  // Date-only strings must be read as a LOCAL calendar date. `new Date("2026-07-14")`
  // parses as UTC midnight, which renders as the previous day in western
  // timezones — the source of "today's update shows as yesterday".
  if (iso.length <= 10) {
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) return iso
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} · ${time}`
}
